import { ARTICLES } from './articles.mjs'

const SITE = 'https://www.statbox.co'

const ANALYSIS =
  'Automated video analysis drafts the stats and highlights. A Statbox analyst then reviews the game before the report is delivered. There is no fixed cutoff, such as 24 hours. You are emailed when it is ready.'

const PRICE =
  'The price depends on whether you run a community, an academy, or a venue. Book a 30-minute demo and we will map the cost to your setup.'

const RECORD =
  'You do not need to buy a camera to start. Play at a venue that already has Statbox cameras, or film the game yourself and upload the MP4.'

const DEMO = { href: '/book-demo', label: 'Book a 30-minute demo' }

export const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
// Legal pages stay reachable but out of the index, so Google does not pick them as sitelinks.
export const LEGAL_ROBOTS = 'noindex, follow'

function page(entry) {
  return {
    image: null,
    answers: [],
    included: [],
    steps: [],
    notes: [],
    related: [],
    cta: DEMO,
    priority: 0.6,
    robots: INDEX_ROBOTS,
    kicker: null,
    ...entry,
    ogTitle: entry.ogTitle || entry.title,
    ogDescription: entry.ogDescription || entry.description,
  }
}

export const PAGES = [
  page({
    path: '/',
    priority: 1,
    title: 'AI Stats and Highlights: Football, Basketball, Padel',
    description:
      'Statbox is the AI sports app that turns your football, basketball, and padel games into player stats, highlight clips, and replays. Free on iOS and Android.',
    ogTitle: 'AI Stats and Highlights: Football, Basketball, Padel',
    ogDescription:
      'AI-powered player stats, highlights, and replays from your own games. Built for players, organisers, academies, and venues.',
    h1: 'Statbox: AI Stats and Highlights for Football, Basketball, and Padel',
    lede:
      'Statbox uses AI video analysis to turn a recording of your own game into player stats, highlight clips, and replays, all in one app for iOS and Android. Communities use it to organise matches and bring players back. Academies use it to review games. Venues use it to offer recording and replays.',
    included: [
      'AI football, basketball, and padel stats from match video',
      'A highlight clip and a stats line for every player',
      'The Statbox app on iOS and Android',
      'Organising tools for communities, academies, and venues',
    ],
    beats: [
      {
        title: 'What each player gets',
        body: 'Statbox turns the match recording into a stats line, a highlight clip, and the full replay for every player. Football stats include goals, assists, shots on target, shots off target, chances created, dribbles, tackles, and saves, for 5-a-side through 11-a-side. Basketball stats include points, shooting, rebounds, and assists. Padel stats follow the rallies and the points that decided the set. Players open the report in the Statbox app on iOS or Android and can share a clip from the game.',
      },
      {
        title: 'Football stats and highlights',
        body: 'A football report names every player on the pitch instead of stopping at a team total. The line can include goals, assists, shots on target, shots off target, chances created, dribbles, tackles, and saves, and the same shape of report covers a 5-a-side booking and an 11-a-side match. Each player also receives a highlight clip cut from that recording, so a goal, a save, or a tackle can be sent on without someone trimming the full match by hand. Organisers use the clip when they call the next kickoff. Players watch it in the Statbox app, next to the replay.',
      },
      {
        title: 'Basketball and padel',
        body: 'Basketball stats follow the box score players already read. Points, shooting, rebounds, and assists are tied to the possessions in the video, so a make or a miss can be watched again instead of sitting as a number with no picture. Padel stats follow the rallies and the points that decided the set. Winners, errors, and the long exchanges are listed against the match video. Football, basketball, and padel all open in the same Statbox app on iOS and Android, with the replay beside the stats and the highlights.',
      },
      {
        title: 'Communities, academies, and venues',
        body: 'A community organiser puts the match on the calendar, names who played, and sends each player a report when the analysis is finished. That report is what groups forward, and it is the reason the same players come back for the next game. An academy keeps the squad, the matches, and the timeline together. A coach filters to one player and presses play on a chance, a shot, or a tackle, then finds that same game still on the player page the following week. A venue can attach the recording to the court booking. Cameras are installed and calibrated once. Staff are not asked to film or to edit, and stats and highlight clips can be offered with the hour the player already booked.',
      },
      {
        title: 'How a game gets analysed',
        body: 'AI video analysis drafts the stats and cuts the highlights from the recording. A Statbox analyst then reviews the game before the report is delivered, so a goal, a basket, or a padel point is not left on the wrong player. There is no fixed cutoff. You are emailed when the report is ready. Play at a venue that already records with Statbox, or film the match and upload the MP4. You do not need to buy a camera to start. Communities use the report to bring players back, academies use it to review sessions, and venues offer the recording with the booking.',
      },
    ],
    faqs: [
      {
        q: 'What is Statbox?',
        a: 'Statbox is an AI sports app that turns video of your own football, basketball, and padel games into player stats, highlight clips, and replays.',
      },
      {
        q: 'How does the AI create stats and highlights?',
        a: 'AI video analysis drafts each player’s stats and cuts their highlights from the match recording. A Statbox analyst reviews the game before the report is delivered to the app.',
      },
      {
        q: 'What football stats do I get?',
        a: 'Goals, assists, shots on and off target, chances created, dribbles, tackles, and saves, plus a highlight clip for every player. It works for 5-a-side up to 11-a-side.',
      },
      {
        q: 'Is there a Statbox app?',
        a: 'Yes. The Statbox app is free to download on iOS and Android. Players use it to join games, open their stats, and share their highlights.',
      },
      {
        q: 'Do I need to buy equipment?',
        a: 'No. Play at a Statbox-enabled venue, or film the game yourself and upload the MP4.',
      },
      {
        q: 'Which sports do you support?',
        a: 'Football, basketball, padel. More coming.',
      },
      {
        q: 'How much does it cost?',
        a: "Depends on your community. We'll tailor it on the call.",
      },
      {
        q: 'How long does setup take?',
        a: 'Most communities run their first game within a week.',
      },
    ],
    notes: [RECORD, PRICE],
    related: [
      { href: '/sports/football', label: 'Football match analysis' },
      { href: '/sports/basketball', label: 'Basketball stats from video' },
      { href: '/sports/padel', label: 'Padel match analysis' },
      { href: '/solutions/communities', label: 'For community organisers' },
      { href: '/solutions/academies', label: 'For academies and coaches' },
      { href: '/solutions/venues', label: 'For venues' },
      { href: '/blog', label: 'Guides to AI sports stats and highlights' },
      { href: '/app-download', label: 'Download the Statbox app' },
      { href: '/blog/ai-football-stats-and-highlights-app', label: 'How AI football stats work' },
      { href: '/blog/how-to-film-football-match-for-ai-analysis', label: 'How to film a football match' },
      { href: '/blog/5-a-side-football-stats-app', label: '5-a-side football stats' },
      { href: '/blog/ai-padel-match-analysis', label: 'Padel analysis guide' },
      { href: '/blog/ai-basketball-stats-from-video', label: 'Basketball stats from a game video' },
      { href: '/docs/api', label: 'Partner API' },
      { href: '/privacy', label: 'Privacy policy' },
      { href: '/terms', label: 'Terms and conditions' },
    ],
    external: [
      {
        href: 'https://apps.apple.com/app/statbox-sports-analytics/id6748250237',
        label: 'Statbox on the App Store',
      },
      {
        href: 'https://play.google.com/store/apps/details?id=com.statbox.app',
        label: 'Statbox on Google Play',
      },
    ],
  }),
  page({
    path: '/book-demo',
    priority: 0.8,
    title: 'Book a Demo — Statbox',
    description: 'Book a free 30-minute Statbox demo. Pick a time that works for you.',
    h1: 'Book a 30-minute Statbox demo',
    lede:
      'Tell us your sport, how many games you run, and what you want players to get. We open Statbox on a real game, then map the setup and the cost.',
    steps: [
      {
        title: 'We hear about your setup',
        body: 'Your sport, your courts, how many games you run a week, and what you want to improve.',
      },
      {
        title: 'A live tour of a real game',
        body: 'You see the stats, highlights, and feed your players would get.',
      },
      {
        title: 'A plan for the first games',
        body: 'What the setup looks like, what it costs, and how the first games get going.',
      },
    ],
    cta: { href: '/book-demo', label: 'Choose a time' },
  }),
  page({
    path: '/app-download',
    priority: 0.8,
    title: 'Download the Statbox App: AI Sports Stats & Highlights | iOS & Android',
    description:
      'Get the Statbox app on iOS or Android. Join games, open your AI-generated football, basketball, and padel stats, and watch your highlights.',
    h1: 'Download the Statbox app',
    lede:
      'Players use the iOS or Android app to join games, open the stats our AI pulls from the match video, and watch their highlights.',
    notes: [
      'App Store: https://apps.apple.com/fr/app/statbox-sports-analytics/id6748250237?l=en-GB',
      'Google Play: https://play.google.com/store/apps/details?id=com.statbox.app',
    ],
    cta: { href: '/app-download', label: 'Get the app' },
  }),
  page({
    path: '/docs/api',
    priority: 0.6,
    title: 'Statbox Partner API Documentation',
    description:
      'Create a Statbox game from a Drive or YouTube link, get a stats CSV and highlight links by email, then pull each player into your own app.',
    h1: 'Statbox Partner API',
    lede:
      'Send a public Google Drive or YouTube link, or ask for an upload link. When the game is ready, you receive a CSV plus highlight and screenshot links. Full reference, including for readers that do not run JavaScript:',
    notes: [
      'HTML reference: https://www.statbox.co/docs/api.html',
      'Markdown reference: https://www.statbox.co/docs/api.md',
      'Statbox analysts process the game. The ready email includes the CSV, each player’s highlight and screenshot, and the game highlight.',
    ],
    cta: { href: '/docs/api.html', label: 'Read the static API reference' },
  }),
  page({
    path: '/privacy',
    priority: 0.3,
    robots: LEGAL_ROBOTS,
    title: 'Privacy Policy — Statbox',
    description:
      'How Statbox collects, uses, and protects personal data when you use the Statbox website, app, and services.',
    h1: 'Privacy Policy',
    lede:
      'This page explains how Statbox collects, uses, and protects personal data when you use the website, the app, and the services.',
    cta: { href: '/privacy', label: 'Read the privacy policy' },
  }),
  page({
    path: '/terms',
    priority: 0.3,
    robots: LEGAL_ROBOTS,
    title: 'Terms and Conditions — Statbox',
    description:
      'Terms and conditions for using the Statbox website, app, and sports analytics services.',
    h1: 'Terms and Conditions',
    lede: 'These terms apply when you use the Statbox website, app, and sports analytics services.',
    cta: { href: '/terms', label: 'Read the terms' },
  }),
page({
    path: '/sports/football',
    priority: 0.9,
    title: 'AI Football Stats & Highlights App | Statbox',
    description:
      'AI football stats and highlights from your own match video. Goals, assists, shots, and a highlight clip for every player in the Statbox app, from 5-a-side to 11-a-side.',
    kicker: 'AI football stats & highlights app',
    h1: 'Your goals, on your phone.',
    lede:
      'Film the match, or play on a pitch we already record. Everyone in the game gets their line, and a highlight they actually want to send.',
    shots: [
      {
        src: '/app-preview/statbox-preview-3.jpeg',
        alt: 'Statbox game highlights and score on a phone, with the match clip and goals, assists, and saves.',
        frame: 'phone',
      },
      {
        src: '/timestamped-replay.png',
        alt: 'Football match analysis with goals, shots, tackles, and other moments listed against the time in the video.',
        frame: 'screen',
        heading: 'Open any moment',
        body: 'They sit on the video. Open a goal, a shot, or a tackle and you are a few seconds either side of it.',
      },
      {
        src: '/app-preview/statbox-preview-1.jpeg',
        alt: 'Statbox match performance rating on a phone, with goals, assists, and what to sharpen next time.',
        frame: 'phone',
        heading: 'Then the rating',
        body: 'Goals, assists, and the one thing to sharpen next time. Same for 11-a-side and 5-a-side.',
      },
    ],
    marksTitle: 'Marked on the match',
    marksBody: 'Open one and you are a few seconds either side of it. Indoor or outdoor, 5-a-side or 11-a-side, as long as the camera can see the players and the goals.',
    marks: ['Goals', 'Assists', 'Shots on target', 'Shots off target', 'Chances created', 'Dribbles', 'Tackles', 'Saves'],
    closer:
      'We price it with you, on a half-hour call. Someone here watches the game before the stats go out, and we email you when it is ready. If you searched for soccer, this is it.',
    related: [
      { href: '/blog/ai-football-stats-and-highlights-app', label: 'How AI football stats work' },
      { href: '/blog/how-to-film-football-match-for-ai-analysis', label: 'How to film your match' },
      { href: '/sports/basketball', label: 'Basketball' },
      { href: '/sports/padel', label: 'Padel' },
      { href: '/solutions/communities', label: 'Running a group' },
    ],
    cta: { href: '/book-demo?audience=community', label: "Let's talk — 30 min" },
  }),
  page({
    path: '/sports/basketball',
    priority: 0.9,
    title: 'AI Basketball Stats & Highlights from Video | Statbox',
    description:
      'AI basketball stats and highlights from your own game video. Points, shooting, rebounds, assists, and the clips to go with them, in the Statbox app.',
    kicker: 'AI basketball stats & highlights',
    h1: 'Your points, on your phone.',
    lede:
      'One recording of the game. Points, threes, rebounds, assists, and the clip, on the same phone. Play on a court we already film, or send the video yourself.',
    shots: [
      {
        frame: 'board',
        items: ['Points', 'Threes', 'Free throws', 'Rebounds', 'Assists', 'Blocks', 'Turnovers', 'Field goals'],
      },
      {
        src: '/app-preview/statbox-preview-2.jpeg',
        alt: 'Statbox physical stats and heart rate on a phone, across the match.',
        frame: 'phone',
        heading: 'Then how the game felt',
        body: 'Steps, distance, and heart rate sit next to the box score. If you are filming it yourself, leave the camera still and keep the court in frame.',
      },
    ],
    closer:
      'Half an hour together and we will tell you what it costs. The numbers are drafted from the video, then a person checks the game before anyone sees them.',
    related: [
      { href: '/blog/ai-basketball-stats-from-video', label: 'How AI basketball stats work' },
      { href: '/sports/football', label: 'Football' },
      { href: '/solutions/academies', label: 'For coaches' },
      { href: '/solutions/venues', label: 'For a court' },
    ],
    cta: { href: '/book-demo?audience=academy', label: "Let's talk — 30 min" },
  }),
  page({
    path: '/sports/padel',
    priority: 0.9,
    title: 'AI Padel Match Analysis, Stats & Highlights | Statbox',
    description:
      'AI padel match analysis from a court camera or your own recording. Winners, errors, the match video, and highlights in the Statbox app.',
    kicker: 'AI padel stats & highlights',
    h1: 'Watch the point back. See who won it.',
    lede:
      'Play on a court we record, or send the match. You get the video, the highlights, and a line for every player.',
    shots: [
      {
        frame: 'board',
        items: ['Winners', 'Aces', 'Smashes', 'Lobs', 'Dropshots', 'Long rallies', 'Double faults', 'Unforced errors'],
        heading: 'The match, broken into points',
        body: 'Not a generic clip. Winners, errors, serves, and the long rallies, with a highlight you can send after you walk off the court.',
      },
    ],
    closer:
      'You do not need to buy a camera to try it. We will walk through a real padel match on the call, including what the recording needs and what it costs. A person checks the match before it is sent.',
    related: [
      { href: '/blog/ai-padel-match-analysis', label: 'How AI padel analysis works' },
      { href: '/solutions/venues', label: 'For a club' },
      { href: '/sports/football', label: 'Football' },
      { href: '/sports/basketball', label: 'Basketball' },
    ],
    cta: { href: '/book-demo?audience=venue', label: "Let's talk — 30 min" },
  }),
  page({
    path: '/solutions/communities',
    priority: 0.9,
    title: 'Organise Football Games and Manage Your Group | Statbox',
    description:
      'Organise the matches, manage the players, and give everyone stats and highlights when the game is over.',
    h1: 'Fill Thursday. Then do it again.',
    lede:
      'Put the game on the calendar, name the players, and give them something to open when they get home.',
    shots: [
      {
        src: '/match-calendar.png',
        alt: 'The Statbox match calendar, with a community game on the day it is played.',
        frame: 'screen',
      },
      {
        src: '/app-preview/statbox-preview-3.jpeg',
        alt: 'The player view after a game: the clip, the score, and their goals, assists, and saves.',
        frame: 'phone',
        heading: 'This is what they send around',
        body: 'The replay, their line, and a highlight. That is the part people show their friends, and the reason the same group turns up next week. If you charge for the game, set a price per player when you create it.',
      },
    ],
    beats: [
      { title: 'Put the game up', body: 'Day, time, and place. Players see it and know where to be.' },
      { title: 'Name who played', body: 'The report is theirs, not a team total with no names.' },
      { title: 'They get the clip', body: 'Replay, their line, and a highlight when the game is done.' },
    ],
    closer:
      'Most groups play their first game within a week. We price it with you. Someone here watches the footage before the stats go out.',
    related: [
      { href: '/blog/5-a-side-football-stats-app', label: 'Stats for your 5-a-side' },
      { href: '/sports/football', label: 'Football' },
      { href: '/sports/basketball', label: 'Basketball' },
      { href: '/solutions/academies', label: 'Academies' },
      { href: '/solutions/venues', label: 'Venues' },
    ],
    cta: { href: '/book-demo?audience=community', label: "Let's talk — 30 min" },
  }),
  page({
    path: '/solutions/academies',
    priority: 0.9,
    title: 'Match Analysis Software for Football and Basketball Academies | Statbox',
    description:
      'Coaches review the match on the timeline, watch the clip, and follow a player from one game to the next.',
    h1: 'Sit with the player. Press play.',
    lede:
      'The squad, the matches, and the exact moment you want to talk about. Football, basketball, or padel.',
    shots: [
      {
        src: '/Dashboard%20Academy%20Platform.png',
        alt: 'The Statbox academy workspace for matches, players, and review.',
        frame: 'screen',
      },
      {
        src: '/timestamped-replay.png',
        alt: 'A match timeline a coach can filter by player and event, then watch the clip.',
        frame: 'screen',
        heading: 'The conversation is the clip',
        body: 'Filter to one player. Watch the chance, the shot, the tackle. Next week that same player is still there, so you are not digging through a camera roll.',
      },
    ],
    beats: [
      { title: 'The squad is there', body: 'Matches and players in one place. Football, basketball, or padel.' },
      { title: 'Press play on the moment', body: 'The chance, the shot, the tackle. You talk about the clip, not a memory.' },
      { title: 'Same player next week', body: 'The last game is still on their page when you sit down again.' },
    ],
    closer:
      'Send the recording, or use a court that already films. The events are drafted from the video, then someone here checks the game before you sit down with the player.',
    related: [
      { href: '/sports/football', label: 'Football' },
      { href: '/sports/basketball', label: 'Basketball' },
      { href: '/solutions/communities', label: 'Groups' },
    ],
    cta: { href: '/book-demo?audience=academy', label: "Let's talk — 30 min" },
  }),
  page({
    path: '/solutions/venues',
    priority: 0.9,
    title: 'Court Recording, Replays and Stats for Sports Venues | Statbox',
    description:
      'Cameras for football pitches and padel courts. Players get the replay with their booking. Stats and highlights are the add-on.',
    h1: 'The court records itself.',
    lede:
      'We install the cameras and calibrate them. The replay can come with the booking. Stats and highlights are what you charge extra for.',
    shots: [
      {
        src: '/Dashboard%20venue%20onwer.png',
        alt: 'The Statbox venue view of games, replays, and bookings.',
        frame: 'screen',
      },
      {
        src: '/app-preview/statbox-preview-3.jpeg',
        alt: 'What a player opens after the booking: the clip, the score, and their stats.',
        frame: 'phone',
        heading: 'What they walk out with',
        body: 'A replay from their hour, and a highlight if they want the full report. Your staff are not filming, and they are not editing. Football, basketball, and padel.',
      },
    ],
    beats: [
      { title: 'Cameras go up once', body: 'We install them and calibrate them to the court.' },
      { title: 'Replay with the booking', body: 'The hour they paid for is the hour they can watch back.' },
      { title: 'Stats are the extra', body: 'Highlights and a player report are what you charge on top.' },
    ],
    closer:
      'Where the cameras go, and what the install costs, depends on the courts. We will show you on a half-hour call, including what you can charge for the replay and the stats. A person checks the game before the report is sent.',
    related: [
      { href: '/sports/padel', label: 'Padel' },
      { href: '/sports/football', label: 'Football' },
      { href: '/docs/api', label: 'Partner API' },
    ],
    cta: { href: '/book-demo?audience=venue', label: "Let's talk — 30 min" },
  }),
  page({
    path: '/blog',
    priority: 0.8,
    title: 'Statbox Blog: AI Sports Stats, Highlights and Match Analysis',
    description:
      'Guides to AI football, basketball, and padel stats: how AI match analysis works, how to film your game, and how to get highlights for every player.',
    h1: 'The Statbox blog',
    lede: 'Guides to AI sports stats, highlights, and match analysis for football, basketball, and padel.',
    related: ARTICLES.map((article) => ({ href: `/blog/${article.slug}`, label: article.h1 })),
    cta: { href: '/app-download', label: 'Download the Statbox app' },
  }),
  ...ARTICLES.map((article) =>
    page({
      path: `/blog/${article.slug}`,
      priority: 0.7,
      title: article.title,
      description: article.description,
      h1: article.h1,
      lede: article.intro,
      article,
      related: article.related,
      cta: { href: '/app-download', label: 'Download the Statbox app' },
    }),
  ),
]

const byPath = new Map(PAGES.map((entry) => [entry.path, entry]))

export function getPage(pathname) {
  const path = !pathname || pathname === '/' ? '/' : String(pathname).replace(/\/+$/, '')
  return byPath.get(path) || null
}

export const HOME_PAGE = byPath.get('/')

export const SITEMAP_ENTRIES = [
  ...PAGES.filter((entry) => entry.robots === INDEX_ROBOTS).map((entry) => ({
    url: entry.path,
    priority: entry.priority,
  })),
  { url: '/docs/api.md', priority: 0.5 },
  { url: '/docs/api.html', priority: 0.5 },
  { url: '/llms.txt', priority: 0.2 },
]

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function renderStaticHtml(entry) {
  const kicker = entry.kicker ? `<small>${escapeHtml(entry.kicker)}</small><br>` : ''
  const parts = [
    `<article class="seo-shell">`,
    `<h1>${kicker}${escapeHtml(entry.h1)}</h1>`,
    `<p>${escapeHtml(entry.lede)}</p>`,
  ]
  const shots = entry.shots || []
  for (const shot of shots) {
    if (shot.src) {
      parts.push(`<p><img src="${escapeHtml(shot.src)}" alt="${escapeHtml(shot.alt || '')}"></p>`)
    }
    if (shot.heading) parts.push(`<h2>${escapeHtml(shot.heading)}</h2>`)
    if (shot.body) parts.push(`<p>${escapeHtml(shot.body)}</p>`)
    if (shot.items) {
      parts.push(`<ul>${shot.items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`)
    }
  }
  if (!shots.length && entry.image) {
    parts.push(`<p><img src="${escapeHtml(entry.image.src)}" alt="${escapeHtml(entry.image.alt)}"></p>`)
  }
  if (entry.included.length && !shots.length) {
    parts.push('<h2>What is included</h2><ul>')
    for (const item of entry.included) parts.push(`<li>${escapeHtml(item)}</li>`)
    parts.push('</ul>')
  }
  if (entry.steps.length && !shots.length) {
    parts.push('<h2>How it works</h2><ol>')
    for (const step of entry.steps) {
      parts.push(`<li><strong>${escapeHtml(step.title)}.</strong> ${escapeHtml(step.body)}</li>`)
    }
    parts.push('</ol>')
  }
  for (const section of entry.article?.sections || []) {
    parts.push(`<h2>${escapeHtml(section.h2)}</h2>`)
    for (const block of section.body) {
      if (typeof block === 'string') parts.push(`<p>${escapeHtml(block)}</p>`)
      else if (block.list) parts.push(`<ul>${block.list.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`)
      else if (block.steps) parts.push(`<ol>${block.steps.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ol>`)
    }
  }
  for (const note of entry.notes) parts.push(`<p>${escapeHtml(note)}</p>`)
  if (entry.marks?.length) {
    if (entry.marksTitle) parts.push(`<h2>${escapeHtml(entry.marksTitle)}</h2>`)
    if (entry.marksBody) parts.push(`<p>${escapeHtml(entry.marksBody)}</p>`)
    parts.push(`<ul>${entry.marks.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`)
  }
  for (const beat of entry.beats || []) {
    parts.push(`<h2>${escapeHtml(beat.title)}</h2><p>${escapeHtml(beat.body)}</p>`)
  }
  if (entry.faqs?.length) {
    parts.push('<h2>Questions about Statbox</h2>')
    for (const faq of entry.faqs) {
      parts.push(`<h3>${escapeHtml(faq.q)}</h3><p>${escapeHtml(faq.a)}</p>`)
    }
  }
  if (entry.closer) parts.push(`<p>${escapeHtml(entry.closer)}</p>`)
  if (entry.related.length) {
    if (!shots.length) parts.push('<h2>Related</h2>')
    parts.push('<ul>')
    for (const link of entry.related) {
      parts.push(`<li><a href="${escapeHtml(link.href)}">${escapeHtml(link.label)}</a></li>`)
    }
    parts.push('</ul>')
  }
  if (entry.external?.length) {
    parts.push('<h2>Get the app</h2><ul>')
    for (const link of entry.external) {
      parts.push(
        `<li><a href="${escapeHtml(link.href)}" rel="noopener noreferrer">${escapeHtml(link.label)}</a></li>`,
      )
    }
    parts.push('</ul>')
  }
  parts.push(`<p><a href="${escapeHtml(entry.cta.href)}">${escapeHtml(entry.cta.label)}</a></p>`)
  parts.push('</article>')
  return parts.join('')
}

function replaceMeta(html, attr, key, content) {
  const tag = `<meta ${attr}="${key}" content="${escapeHtml(content)}" />`
  const single = new RegExp(`<meta\\s+${attr}=["']${key}["']\\s+content=["'][^"']*["']\\s*/?>`, 'i')
  if (single.test(html)) return html.replace(single, tag)
  const multiline = new RegExp(
    `<meta\\s+${attr}=["']${key}["'][\\s\\S]*?content=["'][\\s\\S]*?["']\\s*/?>`,
    'i',
  )
  if (multiline.test(html)) return html.replace(multiline, tag)
  return html
}

export function articleJsonLd(entry, url) {
  const { article } = entry
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.h1,
    description: article.description,
    datePublished: article.published,
    dateModified: article.updated || article.published,
    mainEntityOfPage: url,
    image: `${SITE}/og-image.jpg`,
    author: { '@type': 'Organization', name: 'Statbox', url: `${SITE}/` },
    publisher: { '@id': `${SITE}/#organization` },
  }
}

function bootScript() {
  return '<script>var n=document.querySelector(".seo-shell");if(n)n.remove();</script>'
}

export function applyHtmlShell(html, entry) {
  const url = entry.path === '/' ? `${SITE}/` : `${SITE}${entry.path}`
  let out = html
  out = out.replace(/<title>[^<]*<\/title>/, `<title>${escapeHtml(entry.title)}</title>`)
  out = replaceMeta(out, 'name', 'description', entry.description)
  out = replaceMeta(out, 'property', 'og:title', entry.ogTitle)
  out = replaceMeta(out, 'property', 'og:description', entry.ogDescription)
  out = replaceMeta(out, 'property', 'og:url', url)
  out = replaceMeta(out, 'name', 'twitter:title', entry.ogTitle)
  out = replaceMeta(out, 'name', 'twitter:description', entry.ogDescription)
  out = replaceMeta(out, 'name', 'robots', entry.robots)
  out = replaceMeta(out, 'name', 'googlebot', entry.robots)
  out = out.replace(/<link rel="canonical" href="[^"]*"\s*\/?>/, `<link rel="canonical" href="${url}" />`)

  if (entry.path !== '/') {
    out = out.replace(/<link rel="alternate" type="text\/markdown"[^>]*>\s*/g, '')
    out = out.replace(/<link rel="alternate" type="text\/html"[^>]*>\s*/g, '')
    out = out.replace(/<link rel="alternate" type="text\/plain"[^>]*>\s*/g, '')
    out = out.replace(/<script type="application\/ld\+json" id="home-faq">[\s\S]*?<\/script>\s*/g, '')
  }

  out = out.replace(/<script type="application\/ld\+json" id="seo-faq">[\s\S]*?<\/script>\s*/g, '')
  if (entry.answers.length) {
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: entry.answers.map((answer) => ({
        '@type': 'Question',
        name: answer.q,
        acceptedAnswer: { '@type': 'Answer', text: answer.a },
      })),
    }
    out = out.replace(
      '</head>',
      `<script type="application/ld+json" id="seo-faq">${JSON.stringify(faq)}</script>\n</head>`,
    )
  }

  out = out.replace(/<script type="application\/ld\+json" id="seo-article">[\s\S]*?<\/script>\s*/g, '')
  if (entry.article) {
    out = out.replace(
      '</head>',
      `<script type="application/ld+json" id="seo-article">${JSON.stringify(articleJsonLd(entry, url))}</script>\n</head>`,
    )
  }

  const shell = renderStaticHtml(entry)
  out = out.replace(/<!--seo-shell-->[\s\S]*?<!--\/seo-shell-->/, `<!--seo-shell-->${shell}<!--/seo-shell-->`)
  out = out.replace(/<!--seo-boot-->[\s\S]*?<!--\/seo-boot-->/, `<!--seo-boot-->${bootScript()}<!--/seo-boot-->`)
  return out
}
